import { useQuery, useQueries } from "@tanstack/react-query";
import { Category } from "@/types/homepage";
import callApi from "@repo/next-shared/utils/api";

export async function getCategories(): Promise<Category[]> {
  return await callApi({
    path: "/catalog/category",
    method: "GET",
    noAuth: true,
    // revalidationInterval: 3600 * 24
  });
}

export function useCategoryPageQuery(id: string) {
  return useQueries({
    queries: [
      {
        queryKey: ["category"],
        queryFn: () => getCategories(),
        select: (categories: Category[]) => findCategoryById(categories, id),
      },
    ],
  });
}

export function useCategoriesBase(select: any): any {
  return useQuery({
    queryKey: ["category"],
    queryFn: () => getCategories(),
    select,
    staleTime: 60000 * 60,
  });
}

export function useCategories(): any {
  return useCategoriesBase((categories: Category[]) => categories);
}

export function useCategory(id: string): any {
  return useCategoriesBase((categories: Category[]) =>
    findCategoryById(categories, id),
  );
}

export function useCategoryPath(id: string): any {
  return useCategoriesBase((categories: Category[]) => {
    return findCategoryPathById(categories, id);
  });
}

export const findCategoryById = (
  categories: Category[],
  id: string,
): Category | null => {
  return (
    categories
      .map((category) => {
        return category.id === id
          ? category
          : category.children
            ? findCategoryById(category.children, id)
            : null;
      })
      .find((result) => result !== null) || null
  );
};

export function findCategoryPathById(
  categories: Category[],
  id: string,
): Array<{ path: string; title: string }> {
  return categories.reduce<Array<{ path: string; title: string }>>(
    (acc, cat) => {
      if (acc.length > 0) {
        return acc;
      }

      if (cat.id === id) {
        return [{ path: cat.id, title: cat.title }];
      }

      if (cat.children) {
        const subPath = findCategoryPathById(cat.children, id);
        if (subPath.length > 0) {
          const transformedSubPath = subPath.map((el) => ({
            ...el,
            path: cat.id + "/" + el.path,
          }));
          return [{ path: cat.id, title: cat.title }, ...transformedSubPath];
        }
      }
      return acc;
    },
    [] as Array<{ path: string; title: string }>,
  );
}
